import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ArrowLeft, FileSignature, CheckCircle, Eraser, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ContractSign() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const canvasRef = useRef(null);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  useEffect(() => {
    if (canvasRef.current && contract?.status === 'pending') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = 200;
      ctx.strokeStyle = '#0F172A';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [contract]);

  const fetchContract = async () => {
    try {
      const response = await axios.get(`${API_URL}/contracts/${contractId}`);
      setContract(response.data);
    } catch (error) {
      toast.error('Failed to load contract');
      navigate(isAdmin ? '/admin/drivers' : '/driver/contracts');
    } finally {
      setLoading(false);
    }
  };

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    if (contract?.status === 'signed') return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getMousePos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing || contract?.status === 'signed') return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getMousePos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!agreed) { toast.error('Please agree to the terms and conditions'); return; }
    if (!hasSignature) { toast.error('Please provide your signature'); return; }
    setSigning(true);
    try {
      const signatureData = canvasRef.current.toDataURL('image/png');
      await axios.post(`${API_URL}/contracts/${contractId}/sign`, { signature_data: signatureData });
      toast.success('Contract signed successfully!');
      fetchContract();
    } catch (error) {
      toast.error('Failed to sign contract');
    } finally {
      setSigning(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/contracts/${contractId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contract_${contract?.driver_name || 'driver'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const d = contract?.driver_data || {};

  if (loading) {
    return (<DashboardLayout><div className="text-center py-12 text-muted-foreground">Loading contract...</div></DashboardLayout>);
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="contract-sign-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(isAdmin ? '/admin/drivers' : '/driver/contracts')} data-testid="back-to-contracts">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{contract?.contract_type || 'Service Agreement'}</h1>
              <p className="text-muted-foreground text-sm mt-1">
                {contract?.status === 'signed' ? 'This contract has been signed' : 'Review and sign this contract'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {contract?.status === 'signed' ? (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100 py-1 px-3" data-testid="signed-badge">
                <CheckCircle className="h-4 w-4 mr-1" />
                Signed {contract.signed_at ? new Date(contract.signed_at).toLocaleDateString() : ''}
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 py-1 px-3" data-testid="pending-badge">
                Pending Signature
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="download-pdf-button">
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>

        {/* Contract Document */}
        <Card className="border-slate-300">
          <CardContent className="p-8 md:p-12">
            <div className="contract-body text-sm leading-relaxed text-slate-800 space-y-6" data-testid="contract-content">
              {/* Title */}
              <div className="text-center space-y-1">
                <h2 className="text-lg font-bold uppercase tracking-wide">Owner-Driver Courier Service Agreement</h2>
                <p className="text-slate-500 text-xs">(Independent Contractor Agreement)</p>
              </div>

              <Separator />

              {/* Parties */}
              <div className="space-y-4">
                <p className="text-center font-semibold text-slate-600">Between</p>
                <div className="text-center space-y-0.5">
                  <p className="font-bold">J Smith Trading (Pty) Ltd</p>
                  <p className="text-xs text-slate-500">Registration Number: 2023/211204/07</p>
                  <p className="text-xs text-slate-500">Trading as: JSM Express</p>
                  <p className="text-xs text-slate-500">Address: 131 Rolbal Crescent, Mitchells Plain, Western Cape</p>
                </div>
                <p className="text-center font-semibold text-slate-600">AND</p>
                
                {/* Driver Info Table */}
                <div className="bg-slate-50 border rounded-lg p-4 space-y-2" data-testid="contract-driver-info">
                  <ContractField label="Owner Driver Name" value={d.owner_name || contract?.driver_name} />
                  <ContractField label="ID Number" value={d.owner_id_number} />
                  <ContractField label="Vehicle Make & Model" value={`${d.vehicle_make || ''} ${d.vehicle_model || ''}`.trim()} />
                  <ContractField label="Vehicle Registration Number" value={d.registration_number} />
                  <ContractField label="Driver's Licence Number" value={d.driver_license} />
                  <ContractField label="Contact Number" value={d.owner_contact} />
                </div>
                <ContractField label="Effective Date" value={contract?.created_at ? new Date(contract.created_at).toLocaleDateString('en-ZA') : ''} />
              </div>

              <Separator />

              {/* Section 1 */}
              <Section num="1" title="APPOINTMENT">
                <p>The Company appoints the Owner-Driver to provide last-mile courier and delivery services on behalf of the Company.</p>
                <p>The Owner-Driver accepts the appointment and agrees to perform delivery services in accordance with the terms contained in this Agreement.</p>
              </Section>

              {/* Section 2 */}
              <Section num="2" title="INDEPENDENT CONTRACTOR STATUS">
                <p>The Owner-Driver acknowledges and agrees that:</p>
                <BulletList items={[
                  "They operate as an independent contractor",
                  "They are not an employee of the Company",
                  "They are responsible for their own tax obligations, UIF, PAYE, and statutory payments",
                  "They supply and operate their own vehicle and equipment"
                ]} />
                <p className="mt-2">Nothing in this Agreement shall be interpreted as creating:</p>
                <BulletList items={["An employment relationship", "A partnership", "A joint venture"]} />
                <p className="mt-2">The Owner-Driver has no authority to bind the Company in any contract or agreement.</p>
              </Section>

              {/* Section 3 */}
              <Section num="3" title="SERVICES PROVIDED">
                <p>The Owner-Driver agrees to:</p>
                <BulletList items={[
                  "Deliver parcels assigned by the Company",
                  "Follow route instructions and dispatch procedures",
                  "Handle parcels with care",
                  "Maintain professional conduct with customers",
                  "Follow all operational procedures issued by the Company"
                ]} />
              </Section>

              {/* Section 4 */}
              <Section num="4" title="PERFORMANCE REQUIREMENTS">
                <p>The Owner-Driver must maintain the following performance standard:</p>
                <p className="font-semibold">Minimum delivery success rate: 95%</p>
                <p>Drivers must operate live on the delivery application provided by the Company at all times while performing delivery duties.</p>
                <p>All parcel scans, delivery updates, and status changes must be recorded in real time through the application.</p>
                <p className="mt-2">Failure to meet performance standards may result in:</p>
                <BulletList items={["Suspension from routes", "Reduced delivery allocations", "Termination of this Agreement"]} />
              </Section>

              {/* Section 5 */}
              <Section num="5" title="VEHICLE REQUIREMENTS">
                <p>The Owner-Driver must provide a vehicle that:</p>
                <BulletList items={["Is roadworthy", "Has a valid licence disc", "Is properly maintained", "Meets the operational requirements of the Company"]} />
                <p className="mt-2">The Owner-Driver is responsible for:</p>
                <BulletList items={["Fuel", "Maintenance", "Repairs", "Vehicle insurance (if applicable)", "Traffic fines"]} />
              </Section>

              {/* Section 6 */}
              <Section num="6" title="DRIVER DOCUMENTATION">
                <p>Before operating within the network, the Owner-Driver must provide:</p>
                <BulletList items={["Copy of ID", "Valid driver's licence", "Vehicle registration papers", "Proof of address", "Criminal clearance if required"]} />
                <p className="mt-2">Drivers may not operate until all documentation has been approved by the Company.</p>
              </Section>

              {/* Section 7 */}
              <Section num="7" title="PAYMENT TERMS">
                <p>The Owner-Driver will be compensated based on completed deliveries.</p>
                <p><strong>Payment per parcel:</strong> As per the Company rate card</p>
                <p><strong>Payment cycle:</strong></p>
                <BulletList items={["Monthly payment cycle", "Cut-off period: 21st of each month until the 20th of the following month"]} />
                <p className="mt-2">Only successfully delivered parcels qualify for payment.</p>
                <p className="mt-2">No payment will be made for:</p>
                <BulletList items={["Undelivered parcels", "Fraudulent deliveries", "Parcels lost due to negligence"]} />
              </Section>

              {/* Section 8 */}
              <Section num="8" title="DELIVERY PROCEDURES">
                <p>The Owner-Driver must:</p>
                <BulletList items={[
                  "Deliver parcels to the correct address",
                  "Confirm the recipient's identity",
                  "Follow delivery instructions and procedures",
                  "Operate live on the Company delivery application while on route"
                ]} />
                <p className="mt-2">In the event of an emergency or delivery issue, the Owner-Driver must immediately:</p>
                <BulletList items={["Contact the Distribution Centre (DC)", "Use the direct communication line provided by the Company"]} />
              </Section>

              {/* Section 9 */}
              <Section num="9" title="DELIVERY TO UNKNOWN RECIPIENTS">
                <p>Parcels may only be delivered to:</p>
                <BulletList items={["The intended recipient", "A person authorised by the recipient"]} />
                <p className="mt-2">Drivers may not deliver parcels to:</p>
                <BulletList items={["Unknown persons", "Neighbours", "Security guards", "Any unauthorised third party"]} />
                <p className="mt-2 font-semibold">If a parcel is delivered to an unknown or unauthorised recipient, the Owner-Driver will be deemed negligent and will be liable for the full value of the parcel.</p>
              </Section>

              {/* Section 10 */}
              <Section num="10" title="PROOF OF DELIVERY">
                <p>Drivers must ensure all deliveries include:</p>
                <BulletList items={["Correct address confirmation", "Delivery time", "Recipient confirmation or signature where required"]} />
                <p className="mt-2 font-semibold">False delivery scans or incorrect delivery confirmations will be treated as fraud.</p>
              </Section>

              {/* Section 11 */}
              <Section num="11" title="FAILED DELIVERIES">
                <p>If delivery fails, the Owner-Driver must:</p>
                <BulletList items={["Attempt to contact the recipient", "Record the reason for non-delivery", "Follow Company return procedures"]} />
              </Section>

              {/* Section 12 */}
              <Section num="12" title="RETURN OF PARCELS">
                <p>All undelivered parcels must be returned to the Company depot on the same day.</p>
                <p>Drivers may not:</p>
                <BulletList items={["Keep parcels overnight", "Take parcels home"]} />
                <p className="mt-2">Failure to return parcels may result in disciplinary action or termination.</p>
              </Section>

              {/* Section 13 */}
              <Section num="13" title="PARCEL LIABILITY">
                <p>The Owner-Driver is responsible for parcels while they are in their possession.</p>
                <p>The Owner-Driver will be liable for:</p>
                <BulletList items={["Lost parcels", "Stolen parcels", "Damaged parcels", "Incorrect deliveries"]} />
                <p className="mt-2">The Company may recover the full value of the parcel from the Owner-Driver.</p>
              </Section>

              {/* Section 14 */}
              <Section num="14" title="PAYMENT DEDUCTIONS">
                <p>The Owner-Driver agrees that the Company may deduct from payments due:</p>
                <BulletList items={["Lost parcel costs", "Damaged parcel costs", "Customer compensation caused by driver negligence"]} />
              </Section>

              {/* Section 15 */}
              <Section num="15" title="FRAUD AND MISCONDUCT">
                <p>The following actions are strictly prohibited:</p>
                <BulletList items={[
                  "Marking parcels delivered when they are not delivered",
                  "Delivering parcels to unauthorised persons",
                  "Tampering with parcels",
                  "Keeping parcels"
                ]} />
                <p className="mt-2">Fraud may result in:</p>
                <BulletList items={["Immediate termination", "Legal action", "Criminal charges"]} />
              </Section>

              {/* Section 16 */}
              <Section num="16" title="INSURANCE">
                <p>Drivers must comply with all insurance and safety requirements when operating vehicles.</p>
                <p>Drivers must:</p>
                <BulletList items={["Hold a valid driver's licence", "Operate the vehicle legally", "Follow road safety regulations"]} />
                <p className="mt-2">If insurance claims are rejected due to driver negligence or misconduct, the Owner-Driver will be liable for the damages.</p>
              </Section>

              {/* Section 17 */}
              <Section num="17" title="ACCIDENT REPORTING">
                <p>In the event of an accident, theft, or vehicle damage, the Owner-Driver must:</p>
                <BulletList items={[
                  "Notify the Company immediately",
                  "Report the incident to the South African Police Service if required",
                  "Provide all information necessary for insurance claims"
                ]} />
              </Section>

              {/* Section 18 */}
              <Section num="18" title="ROUTE OWNERSHIP">
                <p>All delivery routes, customers, and delivery work remain the property of the Company.</p>
                <p>The Owner-Driver may not:</p>
                <BulletList items={[
                  "Approach Company clients directly",
                  "Offer courier services independently to Company customers",
                  "Divert work from the Company"
                ]} />
              </Section>

              {/* Section 19 */}
              <Section num="19" title="CONFIDENTIALITY">
                <p>The Owner-Driver agrees not to disclose:</p>
                <BulletList items={["Customer information", "Delivery data", "Company operational procedures"]} />
              </Section>

              {/* Section 20 */}
              <Section num="20" title="DISPUTE RESOLUTION">
                <p>Any dispute arising from this Agreement shall first be resolved through internal negotiation between the parties.</p>
                <p>If the dispute cannot be resolved, it shall be referred to private arbitration in terms of the Arbitration Act of South Africa.</p>
                <p>The parties agree that this Agreement constitutes a commercial independent contractor agreement, and the Owner-Driver waives any claim that they are an employee of the Company.</p>
              </Section>

              {/* Section 21 */}
              <Section num="21" title="TERMINATION">
                <p>Either party may terminate this Agreement by providing 7 days written notice.</p>
                <p>Immediate termination may occur if:</p>
                <BulletList items={["Fraud occurs", "Parcels are stolen", "Company policies are breached"]} />
                <p className="mt-2">Drivers leaving the network without notice may be restricted from future work with the Company.</p>
              </Section>

              {/* Section 22 */}
              <Section num="22" title="GOVERNING LAW">
                <p>This Agreement shall be governed by the laws of the Republic of South Africa.</p>
              </Section>

              <Separator />

              {/* Section 23 - Signatures */}
              <div className="space-y-6">
                <h3 className="font-bold text-base">23. SIGNATURES</h3>
                
                {/* Owner-Driver Signature */}
                <div className="border rounded-lg p-4 space-y-2 bg-slate-50" data-testid="owner-driver-signature-section">
                  <p className="font-bold text-sm">Owner-Driver</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <ContractField label="Name" value={d.owner_name || contract?.driver_name} />
                    <ContractField label="ID Number" value={d.owner_id_number} />
                  </div>
                  
                  {contract?.status === 'signed' && contract.signature_data && (
                    <div className="mt-3">
                      <p className="text-xs text-slate-500 mb-1">Digital Signature:</p>
                      <div className="bg-white border rounded p-2 inline-block" data-testid="signature-image">
                        <img src={contract.signature_data} alt="Signature" className="max-h-[80px]" />
                      </div>
                      <p className="text-xs text-slate-500 mt-1" data-testid="signature-timestamp">
                        Signed on: {new Date(contract.signed_at).toLocaleString('en-ZA', { dateStyle: 'full', timeStyle: 'medium' })}
                      </p>
                    </div>
                  )}
                </div>

                {/* Company Representative */}
                <div className="border rounded-lg p-4 space-y-2" data-testid="company-signature-section">
                  <p className="font-bold text-sm">Company Representative</p>
                  <ContractField label="Name" value="J Smith Trading (Pty) Ltd" />
                  <div className="grid grid-cols-2 gap-x-4">
                    <ContractField label="Signature" value="" />
                    <ContractField label="Date" value="" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Digital Signature Pad (only for pending contracts) */}
        {contract?.status === 'pending' && (
          <Card data-testid="signature-pad-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSignature className="h-5 w-5" />
                Sign This Contract
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Draw your signature in the box below:</p>
                <div className="border-2 border-dashed rounded-lg overflow-hidden bg-white">
                  <canvas
                    ref={canvasRef}
                    className="w-full h-[200px] cursor-crosshair"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    data-testid="signature-canvas"
                  />
                </div>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={clearSignature} data-testid="clear-signature-button">
                    <Eraser className="h-4 w-4 mr-2" />
                    Clear Signature
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <Checkbox id="agree" checked={agreed} onCheckedChange={setAgreed} data-testid="agree-checkbox" />
                <Label htmlFor="agree" className="text-sm leading-relaxed cursor-pointer">
                  I have read and understood all 23 sections of this Owner-Driver Courier Service Agreement. 
                  By signing, I agree to be bound by all the terms stated above.
                </Label>
              </div>

              <Button
                onClick={handleSign}
                className="w-full bg-[#0F172A] hover:bg-[#1e293b]"
                disabled={signing || !agreed || !hasSignature}
                data-testid="submit-signature-button"
              >
                <FileSignature className="h-4 w-4 mr-2" />
                {signing ? 'Signing...' : 'Sign Contract'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Already signed confirmation */}
        {contract?.status === 'signed' && (
          <Card className="border-green-200 bg-green-50" data-testid="signed-confirmation">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-900">Contract Signed</p>
                  <p className="text-sm text-green-700">
                    Signed on {new Date(contract.signed_at).toLocaleString('en-ZA', { dateStyle: 'full', timeStyle: 'medium' })}
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={handleDownloadPDF} data-testid="download-pdf-button-bottom">
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}

// Helper: renders a labeled field with value or underline
const ContractField = ({ label, value }) => (
  <div className="flex items-baseline gap-2 text-sm">
    <span className="text-slate-500 font-medium whitespace-nowrap">{label}:</span>
    {value ? (
      <span className="font-semibold text-slate-900">{value}</span>
    ) : (
      <span className="border-b border-slate-300 flex-1 min-w-[120px]">&nbsp;</span>
    )}
  </div>
);

// Helper: renders a contract section
const Section = ({ num, title, children }) => (
  <div className="space-y-2">
    <h3 className="font-bold text-sm">{num}. {title}</h3>
    <div className="text-sm leading-relaxed space-y-1.5 pl-1">{children}</div>
  </div>
);

// Helper: renders a bullet list
const BulletList = ({ items }) => (
  <ul className="list-disc pl-6 space-y-0.5 text-sm">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);
